"use client";

import { ArrowRight } from "lucide-react";
import Image from "next/image";
import calendarUrl from "public/marketing/icons/calendar.svg";
import clockUrl from "public/marketing/icons/clock.svg";
import contextUrl from "public/marketing/icons/context.svg";
import docUrl from "public/marketing/icons/doc.svg";
import { useEffect, useState } from "react";

import { BgPatternOverlay } from "@/app/(marketing)/_components/bg-pattern-overlay";
import { CopyrightYear } from "@/app/(marketing)/_components/copyright-year";
import { Container } from "@/app/(marketing)/_components/ui/container";

import type { AudienceSlug } from "../_config/audiences";
import { TrackedCtaLink } from "./tracked-cta-link";

interface FunnelFooterProps {
  audience: AudienceSlug;
  ctaHref: string;
  ctaTitle: string;
  ctaSubtitle: string;
  ctaLabel?: string;
}

export default function FunnelFooter({
  audience,
  ctaHref,
  ctaTitle,
  ctaSubtitle,
  ctaLabel = "Claim your profile",
}: FunnelFooterProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      <div className="bg-marketing-blue relative mx-4 mt-4 overflow-hidden rounded-[1.25rem] print:hidden">
        <BgPatternOverlay tileCount={2} />

        <section className="relative overflow-hidden py-20 lg:py-36">
          <style
            dangerouslySetInnerHTML={{
              __html: `
              @keyframes funnelCtaFloat1 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-10px); }
              }
              @keyframes funnelCtaFloat2 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-12px); }
              }
              @keyframes funnelCtaFloat3 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-8px); }
              }
              @keyframes funnelCtaFloat4 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-11px); }
              }
            `,
            }}
          />

          <Container className="relative z-11">
            <div className="mx-auto flex max-w-[678px] flex-col items-center gap-y-6">
              <h2 className="text-center text-4xl! font-medium! text-white sm:text-6xl!">
                {ctaTitle}
              </h2>
              <p className="max-w-[478px] text-center text-lg text-white/80">
                {ctaSubtitle}
              </p>
              <TrackedCtaLink
                href={ctaHref}
                audience={audience}
                ctaLocation="footer"
                className="text-marketing-blue group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium transition-colors hover:bg-white/90"
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </TrackedCtaLink>
            </div>
          </Container>

          <div
            aria-hidden
            className="pointer-events-none relative z-10 hidden lg:block"
          >
            <div
              className="absolute bottom-[70px] left-[8%] will-change-transform"
              style={{
                transform: `translate(${mousePosition.x * 12.5}px, ${mousePosition.y * 9}px)`,
                transition: "transform 0.2s ease-out",
              }}
            >
              <Image
                src={docUrl}
                alt=""
                role="presentation"
                className="h-auto w-[166px]"
                style={{
                  animation: "funnelCtaFloat1 3s ease-in-out infinite",
                }}
              />
            </div>

            <div
              className="absolute top-[-35px] left-[13%] will-change-transform"
              style={{
                transform: `translate(${mousePosition.x * 6}px, ${mousePosition.y * 11}px)`,
                transition: "transform 0.2s ease-out",
              }}
            >
              <Image
                src={contextUrl}
                alt=""
                role="presentation"
                className="h-auto w-[166px]"
                style={{
                  animation: "funnelCtaFloat2 3.5s ease-in-out infinite 0.5s",
                }}
              />
            </div>

            <div
              className="absolute right-[8%] bottom-[70px] will-change-transform"
              style={{
                transform: `translate(${mousePosition.x * -9}px, ${mousePosition.y * -7}px)`,
                transition: "transform 0.2s ease-out",
              }}
            >
              <Image
                src={calendarUrl}
                alt=""
                role="presentation"
                className="h-auto w-[166px]"
                style={{
                  animation: "funnelCtaFloat3 2.8s ease-in-out infinite 1s",
                }}
              />
            </div>

            <div
              className="absolute top-[-35px] right-[13%] will-change-transform"
              style={{
                transform: `translate(${mousePosition.x * -11}px, ${mousePosition.y * -12.5}px)`,
                transition: "transform 0.2s ease-out",
              }}
            >
              <Image
                src={clockUrl}
                alt=""
                role="presentation"
                className="h-auto w-[166px]"
                style={{
                  animation: "funnelCtaFloat4 3.2s ease-in-out infinite 1.5s",
                }}
              />
            </div>
          </div>
        </section>

        <div className="relative z-10 border-t border-white/10">
          <Container>
            <div className="flex justify-center py-8">
              <Image
                src="/brand/wordmark-light.svg"
                alt="Terac"
                // Intrinsic SVG dimensions (viewBox 0 0 175 56). With h-7 w-auto
                // the width follows this ratio, so a mismatched declaration
                // resizes the logo on decode and jitters the layout.
                width={175}
                height={56}
                className="h-7 w-auto brightness-0 invert select-none"
                draggable={false}
              />
            </div>
          </Container>
        </div>
      </div>

      <div className="text-marketing-text-muted flex justify-center px-4 py-6">
        <CopyrightYear />
      </div>
    </>
  );
}
